import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Minimize2, RefreshCw } from 'lucide-react'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { fetchStats, fetchAnalytics, type AdminStats, type AdminAnalytics } from '@/lib/adapters'
import { ErrorState } from '@/components/States'

/**
 * A2.12 — Ekran rejimi (TV panel uchun katta raqamlar).
 *
 * NIMA UCHUN QURILDI (A23/A2 ning boshqa ekranlaridan farqli): bu YANGI
 * endpoint TALAB QILMAYDI. A2.01 dashboard'ining AYNI manbalaridan
 * (`/admin/stats` + `/admin/analytics`) o'qiydi, faqat devor-displey uchun
 * qayta joylashtiradi. A2.02–A2.11 esa ClickHouse/Reverb ustiga quriladi
 * (yo'q) — shu sabab ular soxta «tez kunda» karta bo'lib chizilmaydi.
 * tz/admin/HOLAT.md §2.
 *
 * SKILL §12 (TV panel): katta raqamlar, minimal xrom, avto-yangilanish,
 * oxirgi yangilanish vaqti (§9 real-time widget).
 */

const REFRESH_MS = 15_000

interface Tile {
  key: string
  label: string
  value: number | undefined
}

export function TvScreen() {
  const t = useT()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [updatedAt, setUpdatedAt] = useState<string>('—')
  const [busy, setBusy] = useState(false)
  const acRef = useRef<AbortController | null>(null)

  const load = useCallback(async () => {
    acRef.current?.abort()
    const ac = new AbortController()
    acRef.current = ac
    setBusy(true)
    try {
      const [s, a] = await Promise.all([fetchStats(ac.signal), fetchAnalytics(ac.signal)])
      if (ac.signal.aborted) return
      setStats(s)
      setAnalytics(a)
      setError(null)
      // Faqat displey uchun mahalliy vaqt — solishtirish talab qilinmaydi.
      setUpdatedAt(new Date().toLocaleTimeString())
    } catch (e) {
      if (ac.signal.aborted || (e instanceof DOMException && e.name === 'AbortError')) return
      setError(e instanceof Error ? e : new Error(String(e)))
    } finally {
      if (!ac.signal.aborted) setBusy(false)
    }
  }, [])

  useEffect(() => {
    void load()
    // Real-time endpoint yo'q → so'rovnoma (polling). §9: oxirgi yangilanish ko'rsatiladi.
    const iv = window.setInterval(() => void load(), REFRESH_MS)
    return () => {
      window.clearInterval(iv)
      acRef.current?.abort()
    }
  }, [load])

  const tiles: Tile[] = [
    { key: 'users', label: t.dash.users, value: stats?.users },
    { key: 'dau', label: t.dash.dau, value: analytics?.dau ?? stats?.dau },
    { key: 'mau', label: t.dash.mau, value: analytics?.mau },
    { key: 'msg', label: t.dash.messagesToday, value: stats?.messages_today },
    { key: 'dialogs', label: t.dash.dialogs, value: stats?.dialogs },
    { key: 'reg', label: t.dash.regToday, value: analytics?.registrations.today },
  ]

  return (
    <div className="flex min-h-screen flex-col bg-surface-0 p-6 text-primary">
      {/* Minimal xrom: sarlavha + jonli belgi + oxirgi yangilanish + chiqish */}
      <div className="flex items-center gap-3 pb-6">
        <h1 className="text-lg font-semibold tracking-tight">{t.dash.tvTitle}</h1>
        <span className="flex items-center gap-1.5 text-sm text-secondary">
          <span
            className={cn('h-2 w-2 rounded-full', error ? 'bg-danger' : 'bg-success', busy && 'animate-pulse')}
            aria-hidden
          />
          {error ? t.dash.tvOffline : t.dash.tvLive}
        </span>
        <div className="flex-1" />
        <span className="font-mono text-sm text-tertiary tabular">{t.dash.tvUpdated(updatedAt)}</span>
        <button
          type="button"
          onClick={() => void load()}
          title={t.table.refresh}
          aria-label={t.table.refresh}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-tertiary hover:bg-surface-2 hover:text-primary"
        >
          <RefreshCw size={16} className={busy ? 'animate-spin' : undefined} />
        </button>
        <Link
          to="/"
          title={t.dash.tvExit}
          aria-label={t.dash.tvExit}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-tertiary hover:bg-surface-2 hover:text-primary"
        >
          <Minimize2 size={16} />
        </Link>
      </div>

      {error ? (
        <div className="flex flex-1 items-center justify-center">
          <ErrorState code={error.message} onRetry={() => void load()} />
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map((tile) => (
            <div
              key={tile.key}
              className="flex flex-col justify-center rounded-xl border border-border-default bg-surface-1 p-6"
            >
              {/* SKILL §3: katta raqam mono + tabular-nums (TV masofadan o'qiladi). */}
              <p className="font-mono text-6xl font-semibold tracking-tight tabular xl:text-7xl">
                {tile.value === undefined ? '—' : tile.value.toLocaleString()}
              </p>
              <p className="mt-2 text-md text-secondary">{tile.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
